import { type ReactNode } from 'react';
import { FormattedMessage } from 'react-intl';
import isString from '../../../core/util/isString';
import { memo } from '../../../core/types';
import Table from './Table';
import TableHeader from './TableHeader';
import TableBody from './TableBody';
import styles from './TableRender.module.scss';
import type { RowAlign } from './TableRow';
import TableRow from './TableRow';
import TableHeaderCell from './TableHeaderCell';
import TableCell from './TableCell';

export interface Column<T> {
    /**
     * The key of column
     */
    key: string;
    /**
     * The header of column
     */
    header: ReactNode;
    /**
     * The width of column
     */
    width?: string;
    /**
     * The render method of column
     */
    render: (item: T) => ReactNode;
    /**
     * The vertical align of cell
     */
    verticalAlign?: 'baseline' | 'middle' | 'top' | 'bottom';
}

interface RowItem {
    /**
     * The id of entity
     */
    id: string | number;
}

interface TableRenderProps<T extends RowItem> {
    /**
     * The columns of table
     */
    columns: Column<T>[];
    /**
     * The items of table
     */
    items: T[];
    /**
     * The vertical align of row
     */
    rowAlign?: RowAlign;
    /**
     * Get row key
     */
    getRowKey?(entity: T): string;
    /**
     * Row Click Handler
     */
    onRowClick?(entity: T): void;

    /**
     * The compact style of table
     */
    compact?: boolean;
}

const TableHeadRowsRender = memo(
    <T extends RowItem>({ columns }: Pick<TableRenderProps<T>, 'columns'>) => {
        return (
            <TableRow>
                {columns.map(item => {
                    const { header } = item;
                    if (!header) {
                        return <TableHeaderCell key={item.key} />;
                    }
                    return (
                        <TableHeaderCell key={item.key}>
                            {isString(header) ? (
                                <FormattedMessage id={header} />
                            ) : (
                                header
                            )}
                        </TableHeaderCell>
                    );
                })}
            </TableRow>
        );
    }
);

const TableRowsRender = memo(
    <T extends RowItem>({
        items,
        columns,
        rowAlign,
        getRowKey,
        onRowClick,
    }: Pick<
        TableRenderProps<T>,
        'columns' | 'items' | 'rowAlign' | 'getRowKey' | 'onRowClick'
    >) => {
        return (
            <>
                {items.map(item => (
                    <TableRow
                        verticalAlign={rowAlign}
                        key={getRowKey?.(item) || item.id}
                        onPress={() => {
                            onRowClick?.(item);
                        }}
                    >
                        {columns.map(column => {
                            return (
                                <TableCell
                                    key={column.key}
                                    width={column.width}
                                    verticalAlign={column.verticalAlign}
                                >
                                    {column.render(item)}
                                </TableCell>
                            );
                        })}
                    </TableRow>
                ))}
            </>
        );
    }
);

const TableRender = <T extends RowItem>({
    columns,
    items,
    rowAlign,
    getRowKey,
    onRowClick,
    compact,
}: TableRenderProps<T>) => {
    return (
        <div className={styles.container}>
            <Table stripedReversed compact={compact}>
                <TableHeader dark>
                    <TableHeadRowsRender columns={columns} />
                </TableHeader>
                <TableBody>
                    <TableRowsRender
                        items={items}
                        columns={columns}
                        rowAlign={rowAlign}
                        getRowKey={getRowKey}
                        onRowClick={onRowClick}
                    />
                </TableBody>
            </Table>
        </div>
    );
};

export default memo(TableRender);
